import { DomainRuleViolationError } from "./errors.js";
import { DomainValidationError } from "./identifiers.js";
// Frozen: the database CHECK constraints are generated from these tuples.
export const assignmentTypes = Object.freeze(["PRIMARY", "BACKUP", "TEMPORARY"]);
export const assignmentStatuses = Object.freeze(["ACTIVE", "ENDED"]);
export const assignmentEndCauses = Object.freeze([
    "REASSIGNED",
    "PROMOTED_TO_PRIMARY",
    "ENDED_BY_STAFF",
    "STAFF_DEACTIVATED",
    "ROLE_REVOKED",
    "WINDOW_ELAPSED",
]);
// The customer profile's client-writable preference, validated by a database CHECK built from this.
export const contactChannelPreferences = Object.freeze(["IN_APP", "EMAIL", "PHONE"]);
/**
 * Whether the assignment grants anything at `now`: ACTIVE and inside its window (it starts at
 * `startsAt` inclusive and stops at `endsAt` exclusive). This is the rule the RLS helpers apply.
 */
export function isEffective(assignment, now) {
    return (assignment.status === "ACTIVE" &&
        assignment.startsAt.getTime() <= now.getTime() &&
        (assignment.endsAt === null || assignment.endsAt.getTime() > now.getTime()));
}
// A TEMPORARY may start up to this long before `now`, to tolerate clock skew between callers.
const WINDOW_GRACE_MS = 60_000;
const violation = (code) => new DomainRuleViolationError(code);
/**
 * Every planner starts here: ACTIVE rows whose window has elapsed are ended (`WINDOW_ELAPSED`) and
 * from then on treated as absent, so a lapsed cover neither blocks a slot nor counts as the
 * concierge's row. Rows that are not ACTIVE are ignored.
 */
function prune(active, now) {
    const live = [];
    const ends = [];
    for (const assignment of active) {
        if (assignment.status !== "ACTIVE")
            continue;
        if (assignment.endsAt !== null && assignment.endsAt.getTime() <= now.getTime()) {
            ends.push({ assignmentId: assignment.id, cause: "WINDOW_ELAPSED" });
        }
        else {
            live.push(assignment);
        }
    }
    return { live, ends };
}
const holdingRow = (live, concierge) => live.find((assignment) => assignment.conciergeStaffUserId === concierge);
/** The first PRIMARY of a customer who has none. A concierge who holds BACKUP or TEMPORARY is promoted. */
export function planAssignPrimary(active, input, now) {
    const { live, ends } = prune(active, now);
    if (live.some((assignment) => assignment.type === "PRIMARY")) {
        throw violation("assignment.primary_already_active");
    }
    const held = holdingRow(live, input.conciergeStaffUserId);
    if (held !== undefined)
        ends.push({ assignmentId: held.id, cause: "PROMOTED_TO_PRIMARY" });
    return {
        ends,
        creates: [
            {
                type: "PRIMARY",
                conciergeStaffUserId: input.conciergeStaffUserId,
                startsAt: "now",
                endsAt: null,
                supersedes: null,
            },
        ],
    };
}
/**
 * The modelled PRIMARY transition: a zero-width handover. The old PRIMARY ends and its successor
 * (which links to it through `supersedes`) starts at the same instant, in one write. With
 * `previousPrimaryBecomes: "BACKUP"` the old PRIMARY takes the BACKUP slot, replacing any other
 * BACKUP.
 */
export function planReassignPrimary(active, input, now) {
    if (input.previousPrimaryBecomes !== "ENDED" && input.previousPrimaryBecomes !== "BACKUP") {
        throw new DomainValidationError("previousPrimaryBecomes", "must be ENDED or BACKUP");
    }
    const { live, ends } = prune(active, now);
    const current = live.find((assignment) => assignment.type === "PRIMARY");
    if (current === undefined)
        throw violation("assignment.no_active_primary");
    if (current.conciergeStaffUserId === input.conciergeStaffUserId) {
        throw violation("assignment.same_concierge");
    }
    ends.push({ assignmentId: current.id, cause: "REASSIGNED" });
    const held = holdingRow(live, input.conciergeStaffUserId);
    if (held !== undefined)
        ends.push({ assignmentId: held.id, cause: "PROMOTED_TO_PRIMARY" });
    const toBackup = input.previousPrimaryBecomes === "BACKUP";
    if (toBackup) {
        const otherBackup = live.find((assignment) => assignment.type === "BACKUP" && assignment.id !== current.id && assignment.id !== held?.id);
        if (otherBackup !== undefined)
            ends.push({ assignmentId: otherBackup.id, cause: "REASSIGNED" });
    }
    const creates = [
        {
            type: "PRIMARY",
            conciergeStaffUserId: input.conciergeStaffUserId,
            startsAt: "now",
            endsAt: null,
            supersedes: current.id,
        },
    ];
    if (toBackup) {
        creates.push({
            type: "BACKUP",
            conciergeStaffUserId: current.conciergeStaffUserId,
            startsAt: "now",
            endsAt: null,
            supersedes: null,
        });
    }
    return { ends, creates };
}
/** Configured backup coverage. It never changes the PRIMARY, and the backup is never the PRIMARY. */
export function planAssignBackup(active, input, now) {
    const { live, ends } = prune(active, now);
    if (live.some((assignment) => assignment.type === "BACKUP")) {
        throw violation("assignment.backup_already_active");
    }
    if (holdingRow(live, input.conciergeStaffUserId) !== undefined) {
        throw violation("assignment.concierge_already_assigned");
    }
    return {
        ends,
        creates: [
            {
                type: "BACKUP",
                conciergeStaffUserId: input.conciergeStaffUserId,
                startsAt: "now",
                endsAt: null,
                supersedes: null,
            },
        ],
    };
}
/** Time-boxed cover. It never changes the PRIMARY; one TEMPORARY per customer at a time. */
export function planAddTemporary(active, input, now) {
    const start = input.startsAt.getTime();
    const end = input.endsAt.getTime();
    if (!Number.isFinite(start) ||
        !Number.isFinite(end) ||
        end <= start ||
        start < now.getTime() - WINDOW_GRACE_MS) {
        throw violation("assignment.invalid_window");
    }
    const { live, ends } = prune(active, now);
    if (live.some((assignment) => assignment.type === "TEMPORARY")) {
        throw violation("assignment.temporary_already_active");
    }
    if (holdingRow(live, input.conciergeStaffUserId) !== undefined) {
        throw violation("assignment.concierge_already_assigned");
    }
    return {
        ends,
        creates: [
            {
                type: "TEMPORARY",
                conciergeStaffUserId: input.conciergeStaffUserId,
                startsAt: input.startsAt,
                endsAt: input.endsAt,
                supersedes: null,
            },
        ],
    };
}
/**
 * Ends one assignment on a supervisor's decision. Ending the PRIMARY is allowed and leaves the
 * customer without one (routing falls back to backup, supervisor or the degraded state later).
 */
export function planEndAssignment(active, input, now) {
    const { live, ends } = prune(active, now);
    if (!live.some((assignment) => assignment.id === input.assignmentId)) {
        throw violation("assignment.not_active");
    }
    ends.push({ assignmentId: input.assignmentId, cause: "ENDED_BY_STAFF" });
    return { ends, creates: [] };
}
/**
 * Ends every ACTIVE row of one staff member (deactivation or concierge role revoked). It always
 * succeeds: offboarding is never blockable, even when it removes a customer's PRIMARY.
 */
export function planOffboarding(activeForStaff, cause) {
    if (cause !== "STAFF_DEACTIVATED" && cause !== "ROLE_REVOKED") {
        throw new DomainValidationError("cause", "must be STAFF_DEACTIVATED or ROLE_REVOKED");
    }
    return {
        ends: activeForStaff
            .filter((assignment) => assignment.status === "ACTIVE")
            .map((assignment) => ({ assignmentId: assignment.id, cause })),
        creates: [],
    };
}
